import { useState } from "react";
import { Plus, Trash2, Minus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { EntityCombobox } from "@/components/ui/entity-combobox";
import { Card, CardContent } from "@/components/ui/card";
import {
  useEquipmentContents,
  useAddEquipmentContent,
  useUpdateEquipmentContentQuantity,
  useRemoveEquipmentContent,
} from "@/hooks/useEquipmentContents";
import { useEquipment } from "@/hooks/useEquipment";

interface EquipmentContentListProps {
  equipmentId: string;
}

export const EquipmentContentList = ({
  equipmentId,
}: EquipmentContentListProps) => {
  const { data: contents = [], isLoading } = useEquipmentContents(equipmentId);
  const { data: equipmentData } = useEquipment();
  const allEquipment = equipmentData?.items ?? [];
  const addContent = useAddEquipmentContent();
  const updateQuantity = useUpdateEquipmentContentQuantity();
  const removeContent = useRemoveEquipmentContent();

  const [selectedEquipmentId, setSelectedEquipmentId] = useState<string>("");

  // Filter out already added equipment and the current equipment itself
  const contentIds = contents.map((c) => c.child_equipment_id);
  const availableEquipment = allEquipment.filter(
    (e) => e.id !== equipmentId && !contentIds.includes(e.id),
  );

  const equipmentOptions = availableEquipment.map((e) => ({
    value: e.id,
    label: e.name,
    subtitle: e.manufacturer || undefined,
  }));

  const handleAdd = () => {
    if (selectedEquipmentId) {
      addContent.mutate(
        {
          parentEquipmentId: equipmentId,
          childEquipmentId: selectedEquipmentId,
        },
        { onSuccess: () => setSelectedEquipmentId("") },
      );
    }
  };

  const handleQuantityChange = (contentId: string, quantity: number) => {
    if (quantity >= 1) {
      updateQuantity.mutate({
        id: contentId,
        parentEquipmentId: equipmentId,
        quantity,
      });
    }
  };

  const handleRemove = (contentId: string) => {
    if (confirm("Remove this equipment from contents?")) {
      removeContent.mutate({ id: contentId, parentEquipmentId: equipmentId });
    }
  };

  if (isLoading) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        Loading contents...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Contents ({contents.length})</h3>
      </div>

      <div className="flex gap-2">
        <div className="flex-1">
          <EntityCombobox
            options={equipmentOptions}
            value={selectedEquipmentId}
            onChange={(value) => setSelectedEquipmentId(value || "")}
            placeholder="Search equipment to add..."
            searchPlaceholder="Search by name..."
            testId="equipment-content-picker"
          />
        </div>
        <Button
          onClick={handleAdd}
          disabled={!selectedEquipmentId || addContent.isPending}
          data-testid="add-equipment-content"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add
        </Button>
      </div>

      {contents.length === 0 ? (
        <div className="text-center text-muted-foreground py-8 border rounded-lg">
          No contents added yet. Use the search above to add equipment.
        </div>
      ) : (
        <div className="space-y-2">
          {contents.map((content) => (
            <Card
              key={content.id}
              data-testid={`equipment-content-${content.id}`}
            >
              <CardContent className="p-3 md:p-4 flex flex-col gap-2 md:flex-row md:items-center md:justify-between md:gap-4">
                <div className="min-w-0 flex-1">
                  <p
                    className="font-medium truncate"
                    data-testid={`equipment-content-name-${content.id}`}
                  >
                    {content.child_equipment?.name}
                  </p>
                  <p className="text-sm text-muted-foreground truncate">
                    {content.child_equipment?.manufacturer}
                    {content.child_equipment?.number &&
                      ` • ${content.child_equipment.number}`}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={() =>
                      handleQuantityChange(content.id, content.quantity - 1)
                    }
                    disabled={content.quantity <= 1}
                    data-testid={`decrement-equipment-content-${content.id}`}
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <Input
                    type="number"
                    value={content.quantity}
                    onChange={(e) =>
                      handleQuantityChange(content.id, Number(e.target.value))
                    }
                    className="w-16 text-center"
                    min={1}
                    data-testid={`equipment-content-quantity-${content.id}`}
                  />
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={() =>
                      handleQuantityChange(content.id, content.quantity + 1)
                    }
                    data-testid={`increment-equipment-content-${content.id}`}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => handleRemove(content.id)}
                    data-testid={`remove-equipment-content-${content.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
